"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PLAN_CHANGE_PREFIXES = exports.projectSnapshotDaysSchema = exports.projectSnapshotDaySchema = exports.projectSnapshotAsOfSchema = exports.REPLAYABLE_TYPES = void 0;
exports.shapeOf = shapeOf;
exports.replayBack = replayBack;
exports.replayFloor = replayFloor;
exports.replayTableOf = replayTableOf;
const zod_1 = require("zod");
function shapeOf(entry) {
    if (entry.before === null && entry.after !== null)
        return 'create';
    if (entry.before !== null && entry.after === null)
        return 'delete';
    if (entry.before !== null && entry.after !== null)
        return 'update';
    /* ⚠️ `audit_log_has_content` forbids both being null, so this is unreachable through the
       database. It is still a real branch for anything constructed by hand. */
    return 'unknown';
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Undo every entry, newest first, and hand back the rows as they then were. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * @param live    the rows as they are NOW.
 * @param entries every audit entry for this resource type NEWER than the cut, **newest first**.
 *
 * ⚠️ **The caller filters to one resource type and orders the entries.** Doing it here would mean
 * this function knew about `created_at`, which is the one thing it must not: a kernel that sorts
 * its own input cannot be handed a hand-written sequence in a test.
 */
function replayBack(live, entries) {
    /* ⚠️ A map, because a delete may re-insert a row an earlier-undone entry then modifies. */
    const rows = new Map();
    for (const row of live)
        rows.set(row.id, { ...row });
    for (const entry of entries) {
        const id = idOf(entry);
        if (id === null)
            continue;
        switch (shapeOf(entry)) {
            case 'create':
                /**
                 * ⚠️ **Undoing a create is a delete, and it is unconditional.** The row may not be in the
                 * map at all — it was created after the cut and deleted again before now — in which case
                 * there is nothing to remove and nothing is wrong.
                 */
                rows.delete(id);
                break;
            case 'update':
                /**
                 * ⚠️⚠️ **MERGED, NOT REPLACED, BECAUSE `before` IS A DIFF.** `0006` stores only the fields
                 * that changed, so an update's `before` is `{ hours: "3.00" }` and not a worklog. Replacing
                 * the row with it would leave a row that is one field wide.
                 *
                 * ⚠️ **An update to a row that is not here is IGNORED, not resurrected.** Reaching this
                 * with no row means the entries are not in the order this function documents — a resurrect
                 * here would paper over that and produce a one-field row nobody could trace.
                 */
                {
                    const current = rows.get(id);
                    if (current !== undefined)
                        rows.set(id, { ...current, ...entry.before });
                }
                break;
            case 'delete':
                /**
                 * ⚠️⚠️ **UNDOING A DELETE IS AN INSERT FROM `before`, WHICH IS WHY C61 SPENT A GATE ON
                 * KEEPING IT WHOLE.** Everything the recorded object does not mention is gone — not
                 * defaulted, not null: absent. A task resurrected from `{ id, title }` has no estimate, and
                 * every figure computed from it is quietly wrong on every day before the deletion.
                 */
                if (entry.before !== null)
                    rows.set(id, { ...entry.before, id });
                break;
            case 'unknown':
                break;
        }
    }
    return [...rows.values()];
}
/**
 * ⚠️ **`resourceId` first, then the recorded row's own `id`.** They agree in everything the API
 * writes; a hand-built entry may carry only one, and a delete's `before` is the more authoritative
 * of the two because it is the row itself.
 */
function idOf(entry) {
    if (entry.resourceId !== null && entry.resourceId !== '')
        return entry.resourceId;
    const recorded = entry.before ?? entry.after;
    const id = recorded?.id;
    return typeof id === 'string' && id !== '' ? id : null;
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT MAY TRAVEL, AND WHAT DELIBERATELY MAY NOT.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The resource types a past view reconstructs. A type missing from here is one the replay leaves
 * at its live value — which is honest only where the value is not part of what the view claims.
 *
 * ⚠️⚠️ **RISK NEVER TIME-TRAVELS, AND THE MOCKUP SAYS SO OUT LOUD**: *"A RISK mutató szándékosan
 * NEM utazik — az mindig az élő állapotot mutatja."* A credibility score is a statement about
 * whether TODAY's data can be trusted; as of a past date it would be a score about a dataset
 * nobody was looking at, computed from rules that did not all exist. C60 keeps it live.
 */
exports.REPLAYABLE_TYPES = [
    'project',
    'project_member',
    'task',
    'worklog',
    'contract',
    'line_item',
    'external_cost',
    'invoice',
    'client',
];
/**
 * ⚠️ **How far back a view can honestly go.** Before the oldest audit entry there is no evidence
 * either way, and a replay that runs out of entries silently returns the live rows — which would
 * present *"we have no idea"* as *"nothing had changed yet"*.
 *
 * ⚠️ **The seed backdates its entries** (C61), so on a demo workspace this is months ago rather
 * than the moment the database was created.
 */
function replayFloor(oldestEntryAt) {
    return oldestEntryAt;
}
/* ── the wire, C62 ────────────────────────────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ One project, as of one instant. What the scrubber bar draws.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **COUNTS AND SUMS, NOT ROWS.** The rows are reconstructed in memory for one computation
 * pass and thrown away — P5-2's *"the cut lives for one computation pass only"* — so sending them
 * would be inviting a client to hold a past it could then submit as a write. What crosses the wire
 * is what the bar shows.
 *
 * ⚠️ **`floor` is how far back the view may honestly go.** Before the oldest audit entry there is
 * no evidence either way, and a replay that runs out of entries returns the live rows — which
 * would present *"we have no record"* as *"nothing had changed yet"*.
 */
exports.projectSnapshotAsOfSchema = zod_1.z.object({
    at: zod_1.z.string(),
    floor: zod_1.z.string().nullable(),
    /** ⚠️ How many entries were undone. Zero means the cut is after everything that ever happened. */
    undone: zod_1.z.number().int().nonnegative(),
    taskCount: zod_1.z.number().int().nonnegative(),
    doneTaskCount: zod_1.z.number().int().nonnegative(),
    estimateHours: zod_1.z.string(),
    loggedHours: zod_1.z.string(),
    worklogCount: zod_1.z.number().int().nonnegative(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C62.4 · ONE SNAPSHOT DAY. THE SCRUBBER IS MADE OF THESE.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's scrubber does not slide over a continuum of dates — it snaps to the days a snapshot
 * exists for, draws one tick per day so the gaps are visible, and lists them with a running figure
 * beside each. Sliding over arbitrary dates looks the same and answers a different question: it
 * offers days the view cannot honestly reconstruct, and it hides the ones it can.
 *
 * ⚠️ **`changes` carries ACTIONS, not sentences.** Rule 7: no user-facing string is minted on the
 * server. The client renders a marker when the list is non-empty and translates each action to the
 * same label the audit screen uses.
 */
exports.projectSnapshotDaySchema = zod_1.z.object({
    onDate: zod_1.z.string(),
    /** Hours logged on this project up to and including this day, cumulative. `numeric` as a string. */
    loggedHours: zod_1.z.string(),
    /**
     * ⚠️⚠️ **THE PLAN-SIDE ACTIONS RECORDED ON THIS DAY — the mockup's `TT_PLAN`, read out of the
     * audit log instead of hardcoded.** Its four entries are exactly the events a reader wants to
     * find on a timeline: *Sortétel-keret emelés*, *Feladat-becslések pontosítása*. We do not need a
     * second change log for them, because every one of those is a mutation and every mutation is
     * audited (`decisions.md` P5-3).
     */
    changes: zod_1.z.array(zod_1.z.string()),
});
exports.projectSnapshotDaysSchema = zod_1.z.object({
    /** ⚠️ Ascending, oldest first — the track reads left to right. */
    days: zod_1.z.array(exports.projectSnapshotDaySchema),
});
/**
 * ⚠️⚠️ **WHICH AUDITED ACTIONS COUNT AS *THE PLAN CHANGED*.** Not every mutation is a plan event:
 * a task moved to *folyamatban* is progress, and marking the timeline with it would mark every
 * day. What the mockup marks is the handful of days the commitment itself moved — a line item's
 * budget, a contract's terms, an allocation, or an estimate.
 *
 * ⚠️ **`task.*` is deliberately absent from this list** and is matched separately, by whether the
 * recorded row mentions an estimate. The action alone cannot tell an estimate edit from a status
 * change, and the payload can.
 */
exports.PLAN_CHANGE_PREFIXES = ['lineitem', 'contract', 'allocation'];
/* ── which table an entry is about, C62.2 ─────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE REPLAY KEYS ON THE ACTION, AND KEYING IT ON `resourceType` WAS    │
 * │ STRUCTURALLY WRONG RATHER THAN WRONG IN TWO PLACES.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C62 selected each table's entries by `audit_log.resource_type`, and recorded as a limitation
 * that allocations and rate cards could not travel because theirs say `project` and `member`. A
 * survey of all 176 `recordAudit` calls says the disagreement is not an exception: **79 of them
 * record a resource type that is not their action's subject at all.** That column is a
 * READER-FACING label — it answers *"what am I looking at"* on the audit screen, which is why
 * `allocation.delete` names the project somebody would go looking for.
 *
 * ⚠️⚠️ **THE ACTION IS THE THING THAT NAMES THE TABLE**, and it always was. Its prefix is the
 * domain — `allocation.*`, `ratecard.*`, `lineitem.*` — so the map below is a prefix lookup plus
 * the handful of actions whose prefix belongs to a different table than the row they write.
 *
 * ⚠️ **No migration, and the audit screen is untouched.** Old entries key correctly too, because
 * the action was always recorded and never changed meaning.
 */
const TABLE_OF_PREFIX = {
    project: 'projects',
    task: 'tasks',
    worklog: 'worklogs',
    contract: 'contracts',
    lineitem: 'contract_line_items',
    extcost: 'external_costs',
    invoice: 'invoices',
    client: 'clients',
    allocation: 'allocations',
    ratecard: 'rate_card_assignments',
};
/**
 * ⚠️ **The actions whose PREFIX names a different table than the row they write.** Each is a
 * verb about one thing that changes another: a project's membership, a rate card's template.
 */
const TABLE_OF_ACTION = {
    'project.member_add': 'project_members',
    'project.member_remove': 'project_members',
    'project.member_update': 'project_members',
    /* ⚠️ A TEMPLATE, not an assignment — the prefix would send it to the wrong one. */
    'ratecard.create': 'rate_card_templates',
    'ratecard.update': 'rate_card_templates',
    'ratecard.delete': 'rate_card_templates',
};
/**
 * ⚠️⚠️ **RECORDED AGAINST A TASK, BUT NOT A CHANGE TO THE TASK'S ROW.** A comment, a link and a
 * dependency are written with the task as their resource, and their `before`/`after` describe the
 * comment or the edge. Replayed as task entries, a first comment (`before: null`) read as the
 * task's CREATE and made the task vanish from every day before it, and a deleted comment
 * resurrected a stub task from the comment's fields (found 14 Oct 2026, ux-pass-2).
 */
const NOT_A_ROW_CHANGE = new Set([
    'task.comment',
    'task.comment_edit',
    'task.comment_delete',
    'task.link',
    'task.unlink',
    'task.depend',
    'task.undepend',
]);
/**
 * The table an audit entry is about, or `null` where the replay does not reconstruct it.
 *
 * ⚠️ **`null` is the common answer** and it is not a gap: most audited actions are about
 * settings, integrations, files and sessions, none of which a past view of a project is made of.
 */
function replayTableOf(action) {
    if (NOT_A_ROW_CHANGE.has(action))
        return null;
    const exact = TABLE_OF_ACTION[action];
    if (exact !== undefined)
        return exact;
    const prefix = action.slice(0, action.indexOf('.'));
    return TABLE_OF_PREFIX[prefix] ?? null;
}
//# sourceMappingURL=replay.js.map